import { Check, Zap, Smartphone, Palette, Globe, Users, MessageCircle } from 'lucide-react';

export default function Features() {
  const features = [
    {
      title: "Beautiful & Customizable",
      description: "Create stunning communities that reflect your brand with customizable themes, layouts, and branding options",
      icon: Palette
    },
    {
      title: "Lightning Fast & Scalable", 
      description: "Built for performance and growth - from small groups to massive communities with thousands of active members",
      icon: Zap
    },
    {
      title: "Mobile-First Experience",
      description: "Seamless experience across all devices with native mobile apps and responsive web design for maximum engagement",
      icon: Smartphone
    },
    {
      title: "Global & Accessible",
      description: "Multi-language support, accessibility features, and global infrastructure to connect communities worldwide",
      icon: Globe
    }
  ];

  return (
    <section className="py-20 bg-gray-50" id="whatMakes">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            What Makes Communiticate The 
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Perfect Choice
            </span>
            For Community Building?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover the powerful features that make building and managing communities effortless
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Illustration Side */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-2xl opacity-20"></div>
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
              <div className="grid grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={`aspect-square rounded-2xl ${
                    i === 1 ? 'bg-gradient-to-br from-purple-500 to-blue-500' :
                    i === 2 ? 'bg-gradient-to-br from-blue-500 to-indigo-500' :
                    i === 3 ? 'bg-gradient-to-br from-indigo-500 to-purple-500' :
                    'bg-gradient-to-br from-purple-600 to-pink-500'
                  } flex items-center justify-center relative overflow-hidden`}>
                    {/* Community icons/elements */}
                    <div className="text-white text-center">
                      {i === 1 && <Users className="w-8 h-8 mb-2 mx-auto" />}
                      {i === 2 && <MessageCircle className="w-8 h-8 mb-2 mx-auto" />}
                      {i === 3 && <Zap className="w-8 h-8 mb-2 mx-auto" />}
                      {i === 4 && <Globe className="w-8 h-8 mb-2 mx-auto" />}
                      <div className="text-xs font-medium">
                        {i === 1 && "Connect"}
                        {i === 2 && "Engage"}
                        {i === 3 && "Grow"}
                        {i === 4 && "Scale"}
                      </div>
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute top-2 right-2 w-2 h-2 bg-white/30 rounded-full"></div>
                    <div className="absolute bottom-2 left-2 w-1 h-1 bg-white/40 rounded-full"></div>
                  </div>
                ))}
              </div>
              
              {/* Central connecting element */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-purple-100">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Side */}
          <div className="space-y-8">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
